import { captureErrorMessage } from './analytics/error-reporting';
import { ServerError } from '../types';

/**
 * Wrapper around fetch that throws an error for 5xx response status codes.
 *
 * @throws ServerErrorException if the response status code is 5xx.
 */
export const fetchApi = async (url: string, headers?: HeadersInit) => {
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  });

  if (response.status >= 500) {
    const data = await response.json();
    const message = `Failed to fetch ${url} [${response.status} status]`;
    const errorType = (data?.error as ServerError) || { error: 'internal_server_error' };
    void captureErrorMessage(message, data);
    throw new ServerErrorException(message, response.status, errorType);
  }

  return response;
};

export class ServerErrorException extends Error {
  constructor(public message: string, public status: number, public errorType: ServerError) {
    super(message);
    this.name = 'ServerErrorException';
  }
}

export class NetworkTimeoutError extends Error {
  constructor(public originalError: unknown) {
    const message =
      originalError instanceof Error
        ? `NetworkTimeoutError: ${originalError.message}`
        : 'NetworkTimeoutError';
    super(message);
    this.name = 'NetworkTimeoutError';
  }
}

const isNetworkError = (error: unknown) =>
  error instanceof Error &&
  [
    /failed to fetch/i,
    /networkerror/i,
    /load failed/i,
    /network request failed/i,
    /fetch failed/i,
  ].some(pattern => pattern.test(error.message));

/**
 * Calls fetchApi with automatic retries on transient network errors.
 * Non-network errors (e.g. `ServerErrorException`) are rethrown immediately.
 *
 * @throws `NetworkTimeoutError` after all retry attempts are exhausted.
 */
export async function fetchApiWithNetworkRetry(
  url: string,
  headers?: HeadersInit,
  delayMs = 1000,
  retries = 2
): Promise<Response> {
  try {
    return await fetchApi(url, headers);
  } catch (error) {
    if (!isNetworkError(error)) {
      throw error;
    }

    if (retries <= 0) {
      throw new NetworkTimeoutError(error);
    }

    await new Promise(resolve => setTimeout(resolve, delayMs));
    return fetchApiWithNetworkRetry(url, headers, delayMs * 2, retries - 1);
  }
}
